import {
  CardFilterPrice,
  Deal,
  DealType,
  PackageConfig,
  PriceValue,
  RegionConfig,
} from '@ca/common/types'
import useDeals from './useDeals'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import { useMemo } from 'react'

const sortByPriorityTags = (
  deals: Deal[],
  priorityTags: string[][]
): Deal[] => {
  const tagToBucket = new Map<string, number>()
  priorityTags.forEach((group, i) => {
    group.forEach((tag) => {
      if (!tagToBucket.has(tag)) {
        tagToBucket.set(tag, i)
      }
    })
  })

  // Gets the bucket index for a deal based on its tags, falling back to the last bucket if no tags match
  const getBucket = (deal: Deal) => {
    if (!deal.tags || !deal.tags.length) return priorityTags.length

    return Math.min(
      ...deal.tags.map((tag) => tagToBucket.get(tag) ?? priorityTags.length)
    )
  }

  // Note this also persists the order of deals with the same bucket index
  return [...deals].sort((a, b) => getBucket(a) - getBucket(b))
}

const matchesRequiredTags = (
  deal: Deal,
  requiredTags: string[] | string[][]
): boolean => {
  if (!requiredTags.length) return true
  if (Array.isArray(requiredTags[0])) {
    // AND of OR-groups: every group must have at least one match
    return (requiredTags as string[][]).every((group) =>
      group.some((tag) => deal.tags?.includes(tag))
    )
  }
  // Flat array: AND — deal must have every tag
  return (requiredTags as string[]).every((tag) => deal.tags?.includes(tag))
}

const matchesPrice = ({
  deal,
  price,
  baseDealPrice,
  region,
  packages,
}: {
  deal: Deal
  price?: CardFilterPrice
  baseDealPrice?: number
  region?: RegionConfig | null
  packages: Record<string, PackageConfig>
}) => {
  if (!price || baseDealPrice === undefined) {
    return true
  }

  const packageId = deal.packages[0]
  if (!packageId) {
    return false
  }

  const pkg = packages[packageId]
  if (!pkg) {
    return false
  }

  const dealPrice = PriceValue.safe(pkg.price, region)

  if (price === 'lower_or_even') {
    return dealPrice <= baseDealPrice
  }

  return dealPrice >= baseDealPrice
}

const useDealsByTags = ({
  types,
  hidden,
  requiredTags,
  filterCards,
  tagsExcluded,
  priorityTags,
  price,
  baseDealPrice,
}: {
  types?: DealType[]
  hidden?: boolean
  requiredTags?: string[] | string[][]
  filterCards?: boolean
  tagsExcluded?: string[]
  priorityTags?: string[][]
  price?: CardFilterPrice
  baseDealPrice?: number
}) => {
  const config = useRemoteConfig()
  const deals = useDeals({ hidden, filterCards })
  const dealsByTags = useMemo(() => {
    const source = priorityTags?.length
      ? sortByPriorityTags(deals, priorityTags)
      : deals

    const matchesFilters = (deal: Deal) => {
      const dealType = deal.type ?? 'normal'
      if (types && !types.includes(dealType)) {
        return false
      }

      if (requiredTags && !matchesRequiredTags(deal, requiredTags)) {
        return false
      }

      if (tagsExcluded && tagsExcluded.some((tag) => deal.tags?.includes(tag))) {
        return false
      }

      return matchesPrice({
        deal,
        price,
        baseDealPrice,
        region: config?.region,
        packages: config?.packages ?? {},
      })
    }

    // `all` and `byTags` must derive from the same filtered set, otherwise
    // consumers that fall back to `all` (e.g. no tag selected) bypass filters
    const all = source.filter(matchesFilters)

    const dealsByTag: Record<string, Deal[]> = {}
    all.forEach((deal) => {
      deal.tags?.forEach((tag) => {
        if (!dealsByTag[tag]) {
          dealsByTag[tag] = []
        }
        dealsByTag[tag].push(deal)
      })
    })

    return { byTags: dealsByTag, all }
  }, [deals, types, requiredTags, tagsExcluded, priorityTags, price, baseDealPrice, config])

  return dealsByTags
}

export default useDealsByTags
